import axios from 'axios';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import useModalCarousel from '../hooks/modalCarousel';
import ModalCarousel from '../modalCarousel/ModalCarousel';
import BackLink from '../shared/BackLink';
import { attachImageIntentListeners } from '../shared/protectedImage/imageIntentGestures';
import useImageIntentFriction from '../shared/protectedImage/useImageIntentFriction';
import { selectImageIntentGallery } from '../../../selectors';
import { AlertText, Header, PrimaryButton, Text, Wrapper } from '../v3Styles';
import {
  CarouselImg,
  CarouselImgWrapper,
  CategoryWrapper,
  Grid,
  Image,
  ImageButton,
  ImageWrapper,
  SubHeader,
  ZoomIcon,
} from './yearbookStyles';

const SelectYearbookBackground = ({ history }) => {
  const { open, closeModal, openModal, slideIndex, setSlideIndex } =
    useModalCarousel();
  const shoot = useSelector((state) => state.shoot);
  const studentGallery = useSelector(
    (state) => state.additionalData.studentGallery,
  );
  const currentStudentGalleryId = useSelector(
    (state) => state.additionalData.currentGalleryImageId,
  );
  // The tiles report the order's gallery through the hook's Redux defaults.
  // The confirm dialog is raw HTML outside the hook, so it names the same
  // gallery itself.
  const { getFrictionProps } = useImageIntentFriction({
    surface: 'yearbook_background',
  });
  const dialogGallery = useSelector(selectImageIntentGallery);
  // SelectYearbookPhoto renders poses from this slice, so the selected id always
  // resolves here — additionalData.studentGallery only holds the single gallery an
  // order was initiated from, and is empty on the email/banner entry paths (ENG-2712).
  const poses = useSelector((state) => state.yearbookPoses.poses);
  const date = shoot.yearbookSelectionDeadline;
  // poses can be null while the fetch is in flight, or `{}` for a student with no
  // eligible images, so never assume an array.
  const findPose = (collection) =>
    (Array.isArray(collection) ? collection : []).find(
      (image) => image && image.id === currentStudentGalleryId,
    );
  const selectedImage = findPose(poses) || findPose(studentGallery);

  // Every tile on this screen composites the selected pose over a background,
  // so each is a rendering of the child's photograph. The background id is the
  // per-image throttle discriminator (the pose is the same on every tile).
  const compositeContext = (bg) => ({
    imageKey: `${currentStudentGalleryId}:${bg.id}`,
    surface: 'yearbook_background',
    metadata: {
      image_id: currentStudentGalleryId,
      background_id: bg.id,
      kind: 'yearbook_pose_on_background',
    },
  });

  const updateYearbookImage = async (bgId) => {
    try {
      const response = await axios.patch(
        `/api/v2/yearbook/images/${currentStudentGalleryId}`,
        {
          yearbook_selection: true,
          yearbook_background_id: bgId,
        },
      );
      if (response.status >= 200 && response.status < 300) {
        history.push('/v3/yearbook-selection-complete');
      } else {
        throw new Error('Something went wrong');
      }
    } catch (error) {
      throw new Error('Something went wrong');
    }
  };

  const handleImageClick = (image, bg) => () => {
    let detachDialogListeners = null;
    Swal.fire({
      html: `
        <p class="header">
          You have made your yearbook pose selection
        </p>
        <p class="red italic">
          ${
            shoot.yearbookSelectionInstructions
              ? shoot.yearbookSelectionInstructions
              : ''
          }
        </p>
        <p>
          If this is not the pose you wish to have in the yearbook, please go back and change your selection
        </p>
        <div class="img-wrapper" data-image-intent>
          <img class="bg" src="${bg.image_thumbnail}"/>
          <img src="${image.url}"/>
        </div>
      `,
      confirmButtonText: 'Select and Continue',
      showCancelButton: true,
      // cancelButtonText: 'Cancel',
      customClass: {
        popup: 'popup',
        confirmButton: 'confirm-button',
        cancelButton: 'cancel-button',
      },
      // The dialog's composite is raw HTML, outside React, so it gets the
      // same logging and blocking as the tiles through native listeners.
      didOpen: () => {
        const imgWrapper = document.querySelector('.swal2-popup .img-wrapper');
        if (imgWrapper) {
          detachDialogListeners = attachImageIntentListeners(imgWrapper, {
            ...compositeContext(bg),
            ...dialogGallery,
            surface: 'yearbook_background_confirm',
          });
        }
      },
      willClose: () => detachDialogListeners?.(),
      // Await the PATCH, but route a rejection through Swal's validation
      // channel. A braced body swallows the rejection and closes the modal as
      // though the save succeeded; returning a bare rejected promise instead
      // leaves the modal spinning with both buttons disabled and no message.
      preConfirm: () =>
        updateYearbookImage(bg.id).catch(() => {
          Swal.showValidationMessage(
            'We could not save your yearbook selection. Please try again.',
          );
        }),
    });
  };

  const backgrounds = [];
  shoot.yearbookBackgroundOptions.forEach((bgOption) => {
    backgrounds.push(...bgOption.background_images);
  });

  const handleZoomClick = (bgID) => () => {
    const index = backgrounds.findIndex((bg) => bg.id === bgID);
    setSlideIndex(index);
    openModal();
  };

  // Nothing to composite the backgrounds onto — offer a way back instead of
  // dereferencing an undefined pose and white-screening the whole app.
  if (!selectedImage) {
    return (
      <Wrapper>
        <BackLink history={history} />
        <Wrapper>
          <Header>Select Your Background</Header>
          <Text>
            We couldn&apos;t find the pose you selected. Please go back and
            choose your yearbook pose again.
          </Text>
        </Wrapper>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <BackLink history={history} />
      <Wrapper>
        <Header>Select Your Background</Header>
        <Text>
          Please select the background you would like to be used with your
          selected pose in the yearbook
        </Text>
        <AlertText>{`Yearbook poses must be selected by ${moment(date).format(
          'MMMM DD, YYYY',
        )}`}</AlertText>
        <Wrapper>
          {shoot.yearbookBackgroundOptions.map((bgOption) => (
            <CategoryWrapper key={bgOption.id}>
              <SubHeader>{bgOption.name}</SubHeader>
              <Grid>
                {bgOption.background_images.map((bg) => (
                  <ImageWrapper key={bg.id}>
                    <Image
                      {...getFrictionProps(compositeContext(bg))}
                      background={bg.image_thumbnail}
                      src={selectedImage.url}
                    />
                    {/* TODO: calculate index for backgrounds array */}
                    <ZoomIcon onClick={handleZoomClick(bg.id)} />
                    <ImageButton onClick={handleImageClick(selectedImage, bg)}>
                      Select background
                    </ImageButton>
                  </ImageWrapper>
                ))}
              </Grid>
            </CategoryWrapper>
          ))}
        </Wrapper>
      </Wrapper>

      <ModalCarousel open={open} onClose={closeModal} selectedItem={slideIndex}>
        {backgrounds.map((bg) => (
          <CarouselImgWrapper key={bg.id}>
            <CarouselImg
              {...getFrictionProps(compositeContext(bg))}
              background={bg.image_thumbnail}
              src={selectedImage.url}
            />
            <PrimaryButton onClick={handleImageClick(selectedImage, bg)}>
              Select pose
            </PrimaryButton>
          </CarouselImgWrapper>
        ))}
      </ModalCarousel>
    </Wrapper>
  );
};

SelectYearbookBackground.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    goBack: PropTypes.func.isRequired,
  }).isRequired,
};

export default SelectYearbookBackground;
